import React from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Lock, Monitor, Paintbrush, ArrowRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Plugin, PluginDeployment } from './types';

interface PluginCardProps {
  plugin: Plugin;
  onViewDetails: () => void;
  viewMode: 'mine' | 'public';
}

export function PluginCard({ plugin, onViewDetails, viewMode }: PluginCardProps) {
  const { t } = useTranslation('storefront');
  const latestDeployment = (plugin.deployments || []).sort((a: PluginDeployment, b: PluginDeployment) =>
    b.version.localeCompare(a.version, undefined, { numeric: true }),
  )[0];

  const uniqueStoreCount = plugin.uniqueStoreCount || 0;
  const hasPublicDeployments = plugin.deployments?.some((d) => d.public);
  const hasPrivateDeployments = plugin.deployments?.some((d) => !d.public);
  const isActuallyPrivate = hasPrivateDeployments && !hasPublicDeployments;

  const desktopScreenshot =
    latestDeployment?.demoPreviews?.pages?.[0]?.previews?.[0]?.screenshotUrls?.desktop;

  const featuresArray = (plugin.allFeatures ?? [])
    .map((f) => {
      if (typeof f === 'string') return f;
      if (f && typeof f === 'object' && f.type) return f.type as string;
      return null;
    })
    .filter((f): f is string => f !== null && typeof f === 'string');

  return (
    <div
      className="group cursor-pointer overflow-hidden rounded-lg border bg-white transition-all hover:shadow-md dark:bg-slate-900"
      onClick={onViewDetails}
    >
      {/* Compact screenshot */}
      <div className="relative aspect-[16/9] overflow-hidden bg-slate-50 dark:bg-slate-800">
        {desktopScreenshot ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={desktopScreenshot}
            alt={`${plugin.name} preview`}
            className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-[1.03]"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <Monitor className="text-muted-foreground/30 h-8 w-8" />
          </div>
        )}
        {/* Top-left badges */}
        <div className="absolute left-2 top-2 flex gap-1.5">
          {isActuallyPrivate && (
            <span className="inline-flex items-center gap-1 rounded-md bg-slate-900/70 px-2 py-0.5 text-[10px] font-medium text-white backdrop-blur-sm">
              <Lock className="h-2.5 w-2.5" />
              {t('plugins.card.private')}
            </span>
          )}
          {plugin.isBuilder && (
            <span className="inline-flex items-center gap-1 rounded-md bg-primary/80 px-2 py-0.5 text-[10px] font-medium text-white backdrop-blur-sm">
              <Paintbrush className="h-2.5 w-2.5" />
              {t('plugins.card.editor')}
            </span>
          )}
        </div>
        {/* Hover overlay */}
        <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/30">
          <span className="flex items-center gap-1.5 rounded-full bg-white px-3.5 py-1.5 text-xs font-semibold text-slate-900 opacity-0 shadow-sm transition-opacity group-hover:opacity-100">
            {t('plugins.card.view')}
            <ArrowRight className="h-3 w-3" />
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="px-3.5 py-3">
        <h3 className="group-hover:text-primary line-clamp-1 text-sm font-semibold transition-colors">
          {plugin.name}
        </h3>

        {/* Description */}
        {plugin.description && (
          <p className="text-muted-foreground mt-0.5 line-clamp-1 text-xs">{plugin.description}</p>
        )}

        {/* Features + stats row */}
        <div className="mt-2 flex items-center gap-1.5">
          {featuresArray.slice(0, 2).map((feature, idx) => (
            <Badge
              key={`${feature}-${idx}`}
              variant="secondary"
              className="bg-muted/60 text-muted-foreground h-5 px-1.5 text-[10px] font-medium"
            >
              {feature}
            </Badge>
          ))}
          {featuresArray.length > 2 && (
            <span className="text-muted-foreground/50 text-[10px]">+{featuresArray.length - 2}</span>
          )}
          <span className="flex-1" />
          {uniqueStoreCount > 0 && (
            <span className="text-muted-foreground/60 flex items-center gap-1 text-[10px]">
              <Download className="h-2.5 w-2.5" />
              {uniqueStoreCount}
            </span>
          )}
          <span className="text-muted-foreground/40 text-[10px]">v{latestDeployment?.version}</span>
        </div>
      </div>
    </div>
  );
}
